import React, { useRef, useState, useCallback, useEffect } from 'react';
import './CameraCapture.css';

const CameraCapture = ({ onCapture, onCancel, showOverlay = true }) => {
  const videoRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // rear by default (documents)
  // WYSIWYG: the saved image always matches the preview. If text looks mirrored
  // in the preview, the user taps "Mirror" and both preview + capture flip.
  const [mirror, setMirror] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async (mode) => {
    stopStream();
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: mode }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = mediaStream;
      if (videoRef.current) videoRef.current.srcObject = mediaStream;
      setError(null);
    } catch (err) {
      setError('Unable to access camera. Please check permissions and try again.');
    }
  }, [stopStream]);

  useEffect(() => {
    startCamera(facingMode);
    return () => stopStream();
  }, [facingMode, startCamera, stopStream]);

  const switchCamera = () => {
    setFacingMode((m) => (m === 'environment' ? 'user' : 'environment'));
  };

  const emit = (canvas) => {
    canvas.toBlob((blob) => {
      if (blob && blob.size > 0) {
        onCapture(new File([blob], 'camera-capture.jpg', { type: 'image/jpeg' }));
      }
      stopStream();
    }, 'image/jpeg', 0.95);
  };

  const drawCropped = (video, sx, sy, sw, sh) => {
    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    const ctx = canvas.getContext('2d');
    // Flip horizontally so the saved image matches the mirrored preview.
    if (mirror) { ctx.translate(sw, 0); ctx.scale(-1, 1); }
    ctx.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
    emit(canvas);
  };

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const overlay = overlayRef.current;
    if (showOverlay && overlay) {
      // Map the (cover-fit) overlay box back to source-pixel coordinates.
      const videoRect = video.getBoundingClientRect();
      const actualWidth = video.videoWidth;
      const actualHeight = video.videoHeight;
      const videoRatio = actualWidth / actualHeight;
      const viewRatio = videoRect.width / videoRect.height;

      let renderWidth, renderHeight, offsetX = 0, offsetY = 0;
      if (viewRatio > videoRatio) {
        renderWidth = videoRect.width;
        renderHeight = videoRect.width / videoRatio;
        offsetY = (renderHeight - videoRect.height) / 2;
      } else {
        renderHeight = videoRect.height;
        renderWidth = videoRect.height * videoRatio;
        offsetX = (renderWidth - videoRect.width) / 2;
      }

      const overlayRect = overlay.getBoundingClientRect();
      const scale = actualWidth / renderWidth;
      const sourceX = ((overlayRect.left - videoRect.left) + offsetX) * scale;
      const sourceY = ((overlayRect.top - videoRect.top) + offsetY) * scale;
      const sourceW = overlayRect.width * scale;
      const sourceH = overlayRect.height * scale;
      drawCropped(video, sourceX, sourceY, sourceW, sourceH);
    } else {
      drawCropped(video, 0, 0, video.videoWidth, video.videoHeight);
    }
  };

  const handleClose = () => { stopStream(); onCancel(); };

  if (error) {
    return (
      <div className="camera-overlay">
        <div className="camera-error">
          <p>{error}</p>
          <button type="button" onClick={handleClose} className="camera-btn-cancel-static">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="camera-overlay">
      <div className="camera-container">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="camera-video"
          style={{ transform: mirror ? 'scaleX(-1)' : 'none' }}
          onLoadedMetadata={() => videoRef.current && videoRef.current.play()}
        />

        {showOverlay && (
          <div className="document-guide-overlay">
            <div className="document-frame" ref={overlayRef}>
              <div className="corner top-left"></div>
              <div className="corner top-right"></div>
              <div className="corner bottom-left"></div>
              <div className="corner bottom-right"></div>
              <div className="scan-line"></div>
            </div>
            <p className="guide-text">Align document within the frame</p>
          </div>
        )}

        {/* Top-right utility buttons */}
        <div className="camera-top-controls">
          <button type="button" onClick={switchCamera} className="camera-btn-icon" aria-label="Switch camera" title="Switch camera">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 4h3a2 2 0 0 1 2 2v3"/><path d="M9 20H6a2 2 0 0 1-2-2v-3"/>
              <path d="M20 9a8 8 0 0 0-14-3"/><path d="M4 15a8 8 0 0 0 14 3"/>
            </svg>
          </button>
          <button type="button" onClick={() => setMirror((v) => !v)} className={`camera-btn-icon ${mirror ? 'active' : ''}`} aria-label="Mirror image" title="Mirror (fix flipped text)">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 3v18"/><path d="M8 8 4 12l4 4"/><path d="m16 8 4 4-4 4"/>
            </svg>
          </button>
        </div>

        <div className="camera-controls">
          <button type="button" onClick={handleClose} className="camera-btn-cancel">Cancel</button>
          <button type="button" onClick={handleCapture} className="camera-btn-capture" aria-label="Capture">
            <div className="shutter-inner"></div>
          </button>
          <span className="camera-controls-spacer" />
        </div>
      </div>
    </div>
  );
};

export default CameraCapture;
